import React from "react";
import { Link } from "react-router-dom";

/**
 * WorkspaceErrorBoundary — catches render-time errors inside the v2 surface so
 * a bug in any single page doesn't crash the whole app. Renders a
 * friendly fallback with a "Reload" action and a "Back to Workspace"
 * link. The error message is shown to power users (admins) only — see
 * adminCSS in App.js for the `.admin` class.
 */

class WorkspaceErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to console; Sentry's global addErrorEventListener catches it
    // separately in production.
    console.error("[v2 boundary]", error, info?.componentStack || "");
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="boundary" data-testid="v2-boundary">
        <div className="boundary__icon" aria-hidden="true">⚠️</div>
        <div className="boundary__title">Something went sideways</div>
        <div className="boundary__body">
          The v2 surface hit a render error. Your work is saved in the cloud — you can
          go back to the workspace or try reloading the page.
        </div>
        <div className="boundary__actions">
          <button
            type="button"
            className="boundary__btn boundary__btn--primary"
            onClick={() => { this.reset(); }}
            data-testid="v2-boundary-retry"
          >
            Try again
          </button>
          <Link className="boundary__btn boundary__btn--ghost" to="/v2">
            Back to Workspace
          </Link>
        </div>
        <details className="boundary__details admin">
          <summary>Error details (admin)</summary>
          <pre>{error?.message || String(error)}</pre>
          {error?.stack && <pre>{error.stack}</pre>}
        </details>
      </div>
    );
  }
}

export default WorkspaceErrorBoundary;
